import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

type LogFile = {
  name: string;
  size: number;
  mtime: string;
  isArchive: boolean;
};

@Component({
  selector: 'afoone-logs-view',
  templateUrl: './logs-view.component.html',
  styleUrls: ['./logs-view.component.css'],
})
export class LogsViewComponent implements OnInit, OnDestroy {
  public files: LogFile[] = [];
  public selectedFile = '';
  public content = '';
  public loading = false;
  public tailMode = false;
  public lines = 200;
  public readBytes = 131072;
  public error = '';

  private tailTimer: ReturnType<typeof setInterval> | null = null;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.refreshFiles();
  }

  ngOnDestroy(): void {
    this.stopTail();
  }

  get selected(): LogFile | undefined {
    return this.files.find((f) => f.name === this.selectedFile);
  }

  public async refreshFiles(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      const resp = await firstValueFrom(
        this.http.get<{ files: LogFile[] }>(`${environment.apiUrl}/api/logs`)
      );
      this.files = resp?.files ?? [];
      if (!this.selectedFile && this.files.length > 0) {
        this.selectedFile = this.files[0].name;
      }
    } catch (err: any) {
      this.error = err?.error?.message || 'ERRORS.INTERNAL_SERVER_ERROR';
    } finally {
      this.loading = false;
    }
  }

  public async readNow(): Promise<void> {
    if (!this.selectedFile) return;
    this.stopTail();
    if (this.selected?.isArchive) {
      this.content = '';
      return;
    }
    this.loading = true;
    this.error = '';
    try {
      const url = `${environment.apiUrl}/api/logs/${encodeURIComponent(
        this.selectedFile
      )}/read`;
      const resp = await firstValueFrom(
        this.http.get<{ content: string }>(url, {
          params: { limit: String(this.readBytes) },
        })
      );
      this.content = resp?.content ?? '';
    } catch (err: any) {
      this.error = err?.error?.message || 'ERRORS.INTERNAL_SERVER_ERROR';
    } finally {
      this.loading = false;
    }
  }

  public startTail(): void {
    if (!this.selectedFile || this.selected?.isArchive) return;
    if (this.tailTimer) {
      clearInterval(this.tailTimer);
      this.tailTimer = null;
    }
    this.tailMode = true;
    this.pullTail();
    this.tailTimer = setInterval(() => this.pullTail(), 2000);
  }

  public stopTail(): void {
    this.tailMode = false;
    if (this.tailTimer) {
      clearInterval(this.tailTimer);
      this.tailTimer = null;
    }
  }

  public async downloadSelected(): Promise<void> {
    if (!this.selectedFile) return;
    this.error = '';
    try {
      const url = `${environment.apiUrl}/api/logs/${encodeURIComponent(
        this.selectedFile
      )}/download`;
      const blob = await firstValueFrom(
        this.http.get(url, { responseType: 'blob' })
      );
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = this.selectedFile;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err: any) {
      this.error = err?.error?.message || 'ERRORS.INTERNAL_SERVER_ERROR';
    }
  }

  private async pullTail(): Promise<void> {
    if (!this.selectedFile) return;
    this.loading = true;
    this.error = '';
    try {
      const url = `${environment.apiUrl}/api/logs/${encodeURIComponent(
        this.selectedFile
      )}/read`;
      const resp = await firstValueFrom(
        this.http.get<{ content: string }>(url, {
          params: { tail: '1', lines: String(this.lines) },
        })
      );
      this.content = resp?.content ?? '';
    } catch (err: any) {
      this.error = err?.error?.message || 'ERRORS.INTERNAL_SERVER_ERROR';
      this.stopTail();
    } finally {
      this.loading = false;
    }
  }
}
